const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');

/**
 * Keep the backend awake while someone is using the app.
 *
 * Cloud Run scales the backend to zero when nobody calls it, and a new
 * instance takes the better part of half a minute to answer. In interview 5
 * (2026-09-28) the first backend call of the session was "Let them", and it sat
 * on "Telling them…" for 27 s. Something will always be the first call, so the
 * app makes it itself: once when it opens, then every few minutes while the
 * page is visible and has been touched recently. A tab left open overnight
 * stops pinging, so it never pays to keep a server up for nobody.
 */
export const PING_EVERY_MS = 4 * 60 * 1000; // well inside Cloud Run's idle window
export const IDLE_AFTER_MS = 10 * 60 * 1000; // no taps or keys for this long = nobody is here

export function ping(): void {
  // no auth and no body: the point is only that an instance is up
  fetch(`${BACKEND}/api/kindling/ping`, { cache: 'no-store' }).catch(() => { /* offline is fine */ });
}

/** Should a ping go out now? Pure, for the test. */
export function shouldPing(now: number, lastPing: number, lastActive: number, visible: boolean): boolean {
  return visible && now - lastActive < IDLE_AFTER_MS && now - lastPing >= PING_EVERY_MS;
}

let started = false;

export function keepBackendWarm(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  let lastPing = Date.now();
  let lastActive = Date.now();
  ping();
  const visible = () => document.visibilityState === 'visible';
  const maybe = () => {
    const now = Date.now();
    if (shouldPing(now, lastPing, lastActive, visible())) { lastPing = now; ping(); }
  };
  const active = () => { lastActive = Date.now(); maybe(); };
  for (const ev of ['pointerdown', 'keydown', 'touchstart'] as const) window.addEventListener(ev, active, { passive: true });
  // coming back to the tab (after the phone slept, or from another app) counts as activity
  document.addEventListener('visibilitychange', () => { if (visible()) active(); });
  window.setInterval(maybe, 60 * 1000);
}
